import axios from 'axios';
import { REFRESH_ENDPOINT } from '@root/v2/api/endpoints';
import { PRODUCT_ID } from './constants';
import {
  AuthTokensResponse,
  K1RedirectApiResponse,
  LoginCredentials,
  LoginApiResponse,
  LogoutApiResponse,
  TFASetupApiResponse,
  TFAConfirmApiResponse,
  TFARecoverApiResponse,
  TFAVerifyApiResponse,
  VerifyResetTokenApiResponse,
} from './types';

export const loginApi = async (credentials: LoginCredentials): Promise<LoginApiResponse> => {
  const response = await axios.post<LoginApiResponse>(
    '/api/account/auth/login',
    {
      ...credentials,
      product_id: PRODUCT_ID,
    },
    {
      headers: {
        'Content-Type': 'application/json',
      },
      withCredentials: true,
    },
  );

  return response.data;
};

export const getTFASetupData = async (tfaToken: string): Promise<TFASetupApiResponse> => {
  const response = await axios.post<TFASetupApiResponse>(
    '/api/account/auth/2fa/setup',
    {
      tfa_token: tfaToken,
    },
    {
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    },
  );

  return response.data;
};

export const confirmTFA = async (tfaToken: string, code: string): Promise<TFAConfirmApiResponse> => {
  const response = await axios.post<TFAConfirmApiResponse>(
    '/api/account/auth/2fa/confirm',
    {
      code,
      tfa_token: tfaToken,
    },
    {
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    },
  );

  return response.data;
};

export const verifyTFA = async (tfaToken: string, code: string): Promise<TFAVerifyApiResponse> => {
  const response = await axios.post<TFAVerifyApiResponse>(
    '/api/account/auth/2fa/verify',
    {
      code,
      tfa_token: tfaToken,
    },
    {
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    },
  );

  return response.data;
};

export const recoverTFA = async (tfaToken: string, code: string): Promise<TFARecoverApiResponse> => {
  const response = await axios.post<TFARecoverApiResponse>(
    '/api/account/auth/2fa/recover',
    {
      code,
      tfa_token: tfaToken,
    },
    {
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    },
  );

  return response.data;
};

// Credentials travel so the backend can tie the login it starts to this browser.
export const initiateK1LoginApi = async (): Promise<K1RedirectApiResponse> => {
  const response = await axios.get<K1RedirectApiResponse>('/api/account/auth/k1/redirect', {
    withCredentials: true,
  });

  return response.data;
};

export const exchangeK1CodeApi = async (code: string, state: string): Promise<AuthTokensResponse> => {
  const response = await axios.post<AuthTokensResponse>(
    '/api/account/auth/k1/token',
    { code, state },
    {
      headers: {
        'Content-Type': 'application/json',
      },
      withCredentials: true,
    },
  );

  return response.data;
};

/**
 * Bare axios, because this call invalidates the very tokens the shared client would try to refresh on
 * a 401. Credentials travel so the Drupal session, which the backend keys off the cookie, ends too.
 */
export const logoutApi = async (accessToken: string, refreshToken: string): Promise<LogoutApiResponse> => {
  const response = await axios.post<LogoutApiResponse>(
    '/api/account/auth/logout',
    {},
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
        'X-Refresh-Token': refreshToken,
      },
      withCredentials: true,
    },
  );

  return response.data;
};

/**
 * Bare axios, because a rejected refresh must reach the caller instead of the shared client's 401 handling,
 * which sends the browser to the login page.
 */
export const refreshTokensApi = async (
  accessToken: string | null,
  refreshToken: string,
): Promise<AuthTokensResponse> => {
  const response = await axios.post<AuthTokensResponse>(
    REFRESH_ENDPOINT,
    { access_token: accessToken },
    {
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        'X-Refresh-Token': refreshToken,
      },
      withCredentials: true,
      timeout: 10000,
    },
  );

  return response.data;
};

/**
 * Completes a KaseyaOne single logout. No tokens travel: the backend recovers what it needs from the
 * single-use state it filed when the logout began and which KaseyaOne echoed back to this page.
 */
export const completeK1LogoutApi = async (state: string): Promise<void> => {
  await axios.post(
    '/api/account/auth/k1/logout/callback',
    { state },
    {
      headers: {
        'Content-Type': 'application/json',
      },
    },
  );
};

export const devLoginApi = async (token: string): Promise<AuthTokensResponse> => {
  const response = await axios.post<AuthTokensResponse>(
    '/api/account/auth/dev-login',
    {
      token,
    },
    {
      headers: {
        'Content-Type': 'application/json',
      },
    },
  );

  return response.data;
};

export const forgotPasswordApi = async (email: string): Promise<void> => {
  await axios.post(
    '/api/account/auth/password/reset',
    {
      email,
      product_id: PRODUCT_ID,
    },
    {
      headers: {
        'Content-Type': 'application/json',
      },
    },
  );
};

export const verifyResetTokenApi = async (token: string): Promise<VerifyResetTokenApiResponse> => {
  const response = await axios.get<VerifyResetTokenApiResponse>(
    `/api/account/auth/password/reset/verify/${encodeURIComponent(token)}`,
    {
      headers: {
        Accept: 'application/json',
      },
    },
  );

  return response.data;
};

export const setPasswordApi = async (token: string, password: string, confirmPassword: string): Promise<void> => {
  await axios.post(
    '/api/account/auth/password/set',
    {
      token,
      password,
      confirm_password: confirmPassword,
    },
    {
      headers: {
        'Content-Type': 'application/json',
      },
    },
  );
};
